<template>
  <div class="pt-2 px-1" :id="'comment-' + comment.id">
    <NuxtLink
      v-if="comment.user"
      :to="comment.user.organizationSlug ? '/partners/'+encodeURI(comment.user.organizationSlug) : '/users/' + encodeURI(comment.user.userName)"
      class="flex gap-1 items-center"
    >
      <UserImage :imageId="comment.user.profileImageId" />
      <div>
        <div class="flex gap-1">
          <p class="font-semibold">
            {{ comment.user.organizationName ? comment.user.organizationName : comment.user.userName }}</p>
          <img v-if="comment.user.verified" src="~assets/icon/verified.svg" :alt="$t('accessibility.verified')" />
        </div>
        <p class="text-sm">{{ $d(new Date(comment.createdOn), 'short') }}</p>
      </div>
    </NuxtLink>
    <div v-else class="flex gap-1 items-center">
      <UserImage />
      <div class="flex gap-1">
        <p class="font-semibold">{{ $t('spotdetail.deleted_user_title') }}</p>
      </div>
    </div>
    <div
      ref="commentText"
      v-if="!editing"
      class="comment-text pt-1 overflow-hidden"
      :class="comment.deletedOn ? 'italic text-theme-secondary pl-2' : ''"
      v-linkify:options="{
            target: '_blank'
          }"
      v-html="textHelpers.replaceNewlineWithHtmlTags(comment.text)"

    >
    </div>
    <div v-else class="pt-1 flex flex-col items-end gap-1">
      <textarea ref="editTextArea"
                class="w-full h-fit p-0.5 border-solid border-theme-primary border-[1px] bg-theme-bg min-h-[6em] bg-white"
                v-model="comment.text"
                @input="updateEditTextAreaHeight()"
                @focus="updateEditTextAreaHeight()"
      ></textarea>
      <div class="flex gap-1">
        <ButtonPrimary
          @click="toggleEdit()"
          :small="true"
          v-if="auth.authenticated && commentBelongsToUser()"
        >{{ $t('general.cancel') }}
        </ButtonPrimary
        >
        <ButtonSubmit
          :small="true"
          :loading="uploadingComment"
          :error="uploadingCommentError"
          @click="editComment(comment)"
        />
      </div>
    </div>
    <div
      v-if="comment.deletedOn"
      class="grow flex items-center justify-end text-sm text-theme-secondary pt-1"
    >
      {{
        $t('spotdetail.deleted') +
        ': ' +
        $d(new Date(comment.deletedOn), 'long')
      }}
    </div>
    <div
      v-else-if="comment.editedOn"
      class="grow flex items-center justify-end text-sm text-theme-secondary pt-1"
    >
      {{
        $t('spotdetail.edited') +
        ': ' +
        $d(new Date(comment.editedOn), 'long')
      }}
    </div>
    <div class="flex gap-1 pt-1">
      <CommentVote v-if="!comment.deletedOn" :comment="comment" :voted="voted" />
      <div class="flex gap-0.5 items-center">
        <div>
          <IconComment class="h-[16px]" />
        </div>
      </div>
      <div class="flex gap-1 grow" :class="editing ? 'hidden' : ''">
        <ButtonPrimary
          @click="toggleEdit()"
          :class="editing ? 'invisible' : 'visible'"
          :small="true"
          v-if="auth.authenticated.value && commentBelongsToUser() && !comment.deletedOn"
        >{{ $t('spotdetail.comments_edit_button_title') }}
        </ButtonPrimary
        >
        <ButtonPrimary
          :small="true"
          v-if="auth.authenticated.value && !comment.parentId"
          :class="commentInputHidden ? '' : 'hidden'"
          @click="toggleCommentFormVisibility()"
        >{{ $t('spotdetail.comments_reply_button_title') }}
        </ButtonPrimary>
        <ButtonPrimary
          v-if="auth.authenticated.value && commentBelongsToUser() && !comment.deletedOn && spot.officialAnswer?.id != comment.id"
          @click="showDeleteDialog(comment.id)"
          :small="true"
        >
          {{ $t('spotdetail.delete_comment_button_title') }}
        </ButtonPrimary>
        <Modal :id="'delete_comment_modal_'+comment.id">
          <div class="flex flex-col gap-2">
            <h3>{{ $t('spotdetail.delete_comment_title') }}</h3>
            {{ $t('spotdetail.delete_comment_text') }}
            <ButtonSubmit
              :loading="deletingCommentx"
              :error="deletingCommentError"
              @click="deleteComment(comment)"
              :title="$t('spotdetail.delete_comment_button_title')"
            />
          </div>
        </Modal>
        <CommentOfficialAnswer
          v-if="auth.authenticated.value && commentBelongsToUser() && spot.userData?.canMarkCommentOfficial && !comment.deletedOn"
          :officialdAnswerId="spot.officialAnswer?.id"
          :comment="comment"
          :user="auth.user.value!"
          :spot="spot"
        >
        </CommentOfficialAnswer>
        <div class="grow"></div>
        <CommentReport v-if="auth.authenticated.value && !commentBelongsToUser() && !comment.deletedOn" :spot="spot" :comment="comment" />
      </div>
    </div>
    <div
      v-if="!comment.parentId"
      class="mb-1 ml-4 flex flex-col gap-2 divide-y divide-dashed divide-theme-secondary"
    >
      <div v-if="comment.replies.length > 0"></div>
      <CommentListElement
        v-for="reply of comment.replies"
        :comment="reply"
        :spot="spot"
        class="pr-0"
      />
    </div>
    <Transition>
      <div
        v-if="auth.authenticated.value && !comment.parentId && !commentInputHidden"
        class="comment-input"
        :id="'comment-input-' + comment.id">
        <CommentAdd
          @submit="toggleCommentFormVisibility()"
          @dismiss="toggleCommentFormVisibility()"
          :user="auth.user.value!"
          :spot="spot"
          :comment="comment"
          :allowCancel="true"
        />
      </div>
    </Transition>
  </div>
</template>
<script setup lang="ts">

const auth = useAuth();
const props = defineProps<{ comment: Comment, spot: SpotDetail }>();

const editing = ref(false);
const uploadingCommentError = ref(false);
const uploadingComment = ref(false);
const deletingComment = ref(false);
const deletingCommentError = ref(false);
const editTextArea = ref();
const commentText = ref();
const commentInputHidden = ref(true);
const voted = ref(auth.authenticated && props.spot.userData && props.spot.userData.votedComments ? props.spot.userData.votedComments.includes(props.comment.id) : false);
const comment = ref(props.comment);

function toggleEdit() {
  editing.value = !editing.value;
}

function showDeleteDialog(id: number) {
  document.getElementById('delete_comment_modal_'+id).showModal();
}

function commentBelongsToUser() {
  return comment.value?.user && auth.user.value?.id === comment.value?.user.id;
}

function toggleCommentFormVisibility() {
  commentInputHidden.value = !commentInputHidden.value;
}

function editComment(theComment: Comment) {
  uploadingComment.value = true;

  const formData = new FormData();
  formData.append('text', theComment.text.valueOf());

  const url = '/comments/' + theComment.id.toString() + '/edit';

  useApiAuth().post<Comment>(url, formData)
    .then(function(response) {
      uploadingComment.value = false;
      uploadingCommentError.value = false;
      theComment.text = response.text;
      theComment.editedOn = response.editedOn;
      editing.value = false;
    })
    .catch(function(error) {
      uploadingComment.value = false;
      error.value = true;
      console.log(error);
      editing.value = false;
    });
}

function deleteComment(theComment: Comment) {
  deletingComment.value = true;

  const url = '/comments/' + theComment.id.toString();

  useApiAuth().del(url).then(function(theComment: Comment) {
    deletingComment.value = false;
    comment.value = theComment;
    setTimeout(() => {
      document.getElementById('delete_comment_modal_'+theComment.id).close()
      }, 500
    );
  }).catch(function(error) {
    deletingComment.value = false;
    deletingCommentError.value = true;
  });

}

function updateEditTextAreaHeight() {
  editTextArea.value.style.height = 'auto';
  editTextArea.value.style.height = (5 + editTextArea.value.scrollHeight) + 'px';
}
</script>
<style scoped lang="postcss">

.v-enter-from {
  opacity: 0;
}

.v-enter-to {
  opacity: 1;
}

.v-enter-active {
  transition: opacity 0.2s;
}

.comment-input {
  @apply ml-4 pt-2 border-t border-theme-secondary border-dashed mt-2;
}

:deep(.comment-text) {
  a {
    @apply text-theme-primary underline;
  }
}
</style>
